"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import CountUp from "react-countup";

const statsBase = [
  { number: 0, suffix: "", label: "Check Ins" },     // Will be replaced by API value
  { number: 98, suffix: "%+", label: "Guest Satisfaction Rate" },
  { number: 4.5, suffix: "★", label: "Average Online Rating" },
  { number: 65, suffix: "%+", label: "Average Occupancy Rate" },
  { number: 10, suffix: "M+", label: "Monthly Digital Impressions" },
  { number: 250, suffix: "K+", label: "Social Media Followers" },
  { number: 300, suffix: "+", label: "Trained Hospitality Professionals" },
  { number: 60, suffix: "+", label: "Hotels" },
  { number: 30, suffix: "+", label: "Destinations" },
  { number: 4000, suffix: "+", label: "Corporate Tie Ups" },
  { number: 1200, suffix: "+", label: "Travel Agents" },
  { number: 46, suffix: "+", label: "Sales Force" },
];

const PortfolioStats = () => {
  const [showAll, setShowAll] = useState(false);
  const [checkInCount, setCheckInCount] = useState(() => {
    if (typeof window === "undefined") return 0;
    try {
      const cached = window.sessionStorage.getItem("portfolio_checkins");
      if (!cached) return 0;
      const parsed = JSON.parse(cached);
      if (typeof parsed?.value === "number") return parsed.value;
    } catch {
      // ignore cache errors
    }
    return 0;
  });

  const intervalRef = useRef(null);
  const inFlightRef = useRef(null);

  useEffect(() => {
    const baseUrl = process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL;
    if (!baseUrl) return;

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (inFlightRef.current) {
        inFlightRef.current.abort();
        inFlightRef.current = null;
      }
    };

    const fetchCheckinDetails = async () => {
      if (inFlightRef.current) return;
      const controller = new AbortController();
      inFlightRef.current = controller;

      try {
        const res = await fetch(
          `${baseUrl}/api/property/GetCheckIns`,
          { signal: controller.signal },
        );
        if (!res.ok) return;
        const json = await res.json();
        const nextValue = Number.isFinite(json?.checkInCounts)
          ? json.checkInCounts
          : 0;

        setCheckInCount(nextValue);
        try {
          window.sessionStorage.setItem(
            "portfolio_checkins",
            JSON.stringify({ value: nextValue, ts: Date.now() }),
          );
        } catch {
          // ignore cache errors
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Error fetching property details:", error);
        }
      } finally {
        inFlightRef.current = null;
      }
    };

    const startPolling = () => {
      if (intervalRef.current) return;
      fetchCheckinDetails(); // immediate refresh
      intervalRef.current = setInterval(fetchCheckinDetails, 60000);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    // Start only if the tab is visible (avoids background work during load).
    if (!document.hidden) startPolling();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      stopPolling();
    };
  }, []);

  // Replace the first stat number with checkInCount
  const stats = useMemo(
    () => [{ ...statsBase[0], number: checkInCount }, ...statsBase.slice(1)],
    [checkInCount],
  );

  const isMobile =
    typeof window !== "undefined" && window.innerWidth <= 768;

  const visibleCount = isMobile && !showAll ? 4 : stats.length;
  const statsToShow = stats.slice(0, visibleCount);

  const toggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  return (
    <section>
      <div className="alt-two">
        <h1 className="mainheading custom-hotel-header mb-5">OUR PORTFOLIO</h1>
      </div>

      <div className="container">
        <div className="row text-center gy-4">
          {statsToShow.map((item, index) => (
            <div key={index} className="col-6 col-md-3 col-lg-2">
              <div className="stat-circle mx-auto">
                <h5 className="mb-1 text-dark">
                  <CountUp
                    end={item.number}
                    duration={2}
                    separator=","
                    suffix={item.suffix}
                    decimals={Number.isInteger(item.number) ? 0 : 1}
                  />
                </h5>
                <p className="mb-0 text-dark custom-sub-description">
                  {item.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        {isMobile && (
          <div className="text-center mt-4">
            <button
              className="btn btn-primary"
              style={{ background: "#f35b04", color: "white" }}
              onClick={toggleShowAll}
            >
              {showAll ? "View Less" : "View More"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default PortfolioStats;
